import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { CheckCircle2 } from 'lucide-react';

export default function AuthPage({ setIsAuthenticated, setUser }) {
  const navigate = useNavigate();

  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
  });
  const [loading, setLoading] = useState(false);

  // Email verification UX
  const [pendingVerifyEmail, setPendingVerifyEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setPendingVerifyEmail('');

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';

      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : { ...formData, origin_url: window.location.origin };

      const response = await axios.post(`${API}${endpoint}`, payload);

      // ✅ REGISTER: backend returns message + user (no token) until verified
      if (!isLogin) {
        const user = response.data?.user;

        setPendingVerifyEmail(formData.email);
        toast.success(response.data?.message || 'Account created! Please verify your email to continue.');

        // Clear password for safety
        setFormData({ email: formData.email, password: '', name: '' });
        return;
      }

      // ✅ LOGIN: backend returns token + user (only if verified)
      const token = response.data?.token;
      const user = response.data?.user;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      setUser(user);
      setIsAuthenticated(true);

      toast.success('Welcome back!');
      navigate('/crm', { replace: true });
    } catch (error) {
      const status = error.response?.status;

      // ✅ Not verified
      if (status === 403) {
        setPendingVerifyEmail(formData.email);
        toast.error(error.response?.data?.detail || 'Email not verified. Please check your inbox.');
        return;
      }

      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!pendingVerifyEmail) return;

    setResendLoading(true);
    try {
      await axios.post(`${API}/auth/resend-verification-public`, {
        email: pendingVerifyEmail,
        origin_url: window.location.origin,
      });

      toast.success('If that email exists, we sent a new verification link.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not resend verification email');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[#F9F9F7] p-6">
      <div className="noise-overlay" />
      <Card className="w-full max-w-md border-gray-100 shadow-lg">
        <CardHeader className="space-y-4 pb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#1A4D2E] to-[#14532D] flex items-center justify-center shadow-lg">
              <CheckCircle2 className="w-8 h-8 text-white" />
            </div>
          </div>
          <div className="text-center">
            <CardTitle className="text-3xl font-bold tracking-tight text-gray-900">
              Don't Forget
            </CardTitle>
            <CardDescription className="text-base mt-2">
              {isLogin
                ? "Let's see who needs your attention today."
                : 'Start remembering what matters most.'}
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent>
          {/* Email verification notice */}
          {pendingVerifyEmail && (
            <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm text-amber-900 font-medium">Verify your email to continue</p>
              <p className="text-xs text-amber-800 mt-1">
                We sent a verification link to{' '}
                <span className="font-semibold">{pendingVerifyEmail}</span>. Open your inbox and click “Verify Email”.
              </p>

              <Button
                type="button"
                disabled={resendLoading}
                onClick={handleResendVerification}
                className="mt-3 w-full h-10 rounded-full bg-amber-600 hover:bg-amber-700 text-white"
              >
                {resendLoading ? 'Sending…' : 'Resend verification email'}
              </Button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="name" className="text-gray-700 font-medium">
                  Full Name
                </Label>
                <Input
                  id="name"
                  data-testid="register-name-input"
                  type="text"
                  required={!isLogin}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="h-11 rounded-lg border-gray-200 bg-gray-50 focus:bg-white"
                  placeholder="John Doe"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-700 font-medium">
                Email Address
              </Label>
              <Input
                id="email"
                data-testid="auth-email-input"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="h-11 rounded-lg border-gray-200 bg-gray-50 focus:bg-white"
                placeholder="you@example.com"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-gray-700 font-medium">
                  Password
                </Label>
                {isLogin && (
                  <Link to="/forgot-password" className="text-xs text-[#1A4D2E] hover:underline">
                    Forgot password?
                  </Link>
                )}
              </div>
              <Input
                id="password"
                data-testid="auth-password-input"
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="h-11 rounded-lg border-gray-200 bg-gray-50 focus:bg-white"
                placeholder="••••••••"
              />
            </div>

            <Button
              type="submit"
              data-testid="auth-submit-button"
              disabled={loading}
              className="w-full h-11 rounded-full bg-[#1A4D2E] hover:bg-[#14532D] text-white shadow-md hover:shadow-lg transition-all duration-300 font-medium"
            >
              {loading ? 'Please wait…' : isLogin ? 'Sign In' : 'Create Account'}
            </Button>
          </form>

          {!isLogin && (
            <p className="text-xs text-center text-gray-500 mt-3">7-day free trial. No credit card required.</p>
          )}

          <div className="mt-6 text-center">
            <button
              type="button"
              data-testid="auth-toggle-button"
              onClick={() => {
                setIsLogin(!isLogin);
                setPendingVerifyEmail('');
              }}
              className="text-sm text-gray-600 hover:text-[#1A4D2E] transition-colors font-medium"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
